import { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';

import { DrawerV2, ButtonV2, dsV2 } from '@bloomays-lib/ui.shared';
import { SEARCH_SOCIETY, LINK_SOCIETY_TO_MISSION, UNLINK_SOCIETY_TO_MISSION } from '@bloomays-lib/adapter.api-bloomer';

import { cancel, notify } from '../../../../helpers/toastify';
import { FieldV2, InputV2, InfoV2 } from '../formFields';

export type ChangeBloomerSocietyDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** Ouvre le formulaire de création d'une nouvelle société (délégué au drawer d'édition en mode création). */
  onCreateNew: () => void;
  missionRecordId: string;
  currentSocietyId?: string;
};

/**
 * Changer la société du Bloomer de façon SÛRE : rechercher une société fournisseur EXISTANTE à
 * rattacher (via `linkSocietyToMission` : setLinks, remplace la société sans réécrire ses coordonnées)
 * OU créer une nouvelle société. L'ancienne société est détachée explicitement (pas d'unlink en admin).
 * Évite d'écraser une société de portage partagée en éditant ses champs en place.
 */
export const ChangeBloomerSocietyDrawer = ({
  open,
  onClose,
  onSaved,
  onCreateNew,
  missionRecordId,
  currentSocietyId,
}: ChangeBloomerSocietyDrawerProps) => {
  const { t } = useTranslation(['missionDashboard', 'notify']);
  const [query, setQuery] = useState('');

  const [searchSocieties, { data, loading }] = useLazyQuery(SEARCH_SOCIETY);
  const [linkSociety, { loading: linking }] = useMutation(LINK_SOCIETY_TO_MISSION);
  const [unlinkSociety] = useMutation(UNLINK_SOCIETY_TO_MISSION);

  // Recherche des sociétés fournisseur (isClient:false) — lodash debounce 500 ms, min 3 caractères.
  const debouncedSearch = useMemo(
    () => debounce((q: string) => void searchSocieties({ variables: { query: q, isClient: false } }), 500),
    [searchSocieties],
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);
  useEffect(() => {
    const q = query.trim();
    if (q.length < 3) {
      debouncedSearch.cancel();
      return;
    }
    debouncedSearch(q);
  }, [query, debouncedSearch]);

  const tooShort = query.trim().length < 3;
  const results = ((data?.searchSociety ?? []) as { recordId: string; name?: string; siret?: string }[])
    .filter((soc) => soc && soc.recordId && soc.recordId !== currentSocietyId)
    .slice(0, 8);

  const attachExisting = async (societyId: string) => {
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      // Rattache la société choisie (setLinks : remplace la société Bloomer, sans réécrire ses champs).
      await linkSociety({ variables: { societyId, missionId: missionRecordId, role: 'bloomerBilling' } });
      // En mode admin le lien ne détache pas l'ancienne société → on l'appelle explicitement.
      if (currentSocietyId && currentSocietyId !== societyId) {
        await unlinkSociety({
          variables: { societyId: currentSocietyId, missionId: missionRecordId, role: 'bloomerBilling' },
        });
      }
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onSaved();
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={t('drawers.editBloomerSociety.changeTitle', { ns: 'missionDashboard' })}
      width={520}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('drawers.common.cancel', { ns: 'missionDashboard' })}
          </ButtonV2>
          <ButtonV2 startIcon={<AddRoundedIcon sx={{ fontSize: 15 }} />} onClick={onCreateNew}>
            {t('drawers.editBloomerSociety.createNew', { ns: 'missionDashboard' })}
          </ButtonV2>
        </>
      }
    >
      <InfoV2 tone="blue">{t('drawers.editBloomerSociety.changeIntro', { ns: 'missionDashboard' })}</InfoV2>

      <FieldV2
        label={t('drawers.editBloomerSociety.searchBloomerSociety', { ns: 'missionDashboard' })}
        hint={t('drawers.editClientSociety.searchClientHint', { ns: 'missionDashboard' })}
      >
        <InputV2
          value={query}
          onChange={setQuery}
          placeholder={t('drawers.editClientSociety.searchClientPlaceholder', { ns: 'missionDashboard' })}
          ariaLabel="change-bloomer-society-search"
        />
      </FieldV2>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: 300, overflowY: 'auto' }}>
        {tooShort ? (
          <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
            {t('drawers.editClientSociety.searchTooShort', { ns: 'missionDashboard' })}
          </Box>
        ) : loading ? (
          <Box sx={{ fontSize: 12, color: dsV2.color.t4, padding: '8px 0' }}>
            {t('drawers.common.searching', { ns: 'missionDashboard' })}
          </Box>
        ) : results.length === 0 ? (
          <Box sx={{ fontSize: 12, color: dsV2.color.t4, fontStyle: 'italic', padding: '8px 0' }}>
            {t('drawers.editClientSociety.noSocietyFound', { ns: 'missionDashboard' })}
          </Box>
        ) : (
          results.map((soc) => (
            <Box
              key={soc.recordId}
              role="button"
              onClick={() => (linking ? undefined : attachExisting(soc.recordId))}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                padding: '9px 11px',
                cursor: linking ? 'progress' : 'pointer',
                borderRadius: dsV2.radius.sm,
                border: `1px solid ${dsV2.color.border}`,
                background: dsV2.color.blanc,
                '&:hover': { borderColor: dsV2.color.bleu, background: dsV2.color.bleuDim },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: '12.5px', fontWeight: 600, color: dsV2.color.noir }}>{soc.name}</Box>
                {soc.siret ? (
                  <Box sx={{ fontSize: 11, color: dsV2.color.t4 }}>
                    {t('drawers.editClientSociety.siretLabel', { ns: 'missionDashboard', siret: soc.siret })}
                  </Box>
                ) : null}
              </Box>
              <ButtonV2 variant="secondary" size="small" loading={linking} disabled={linking}>
                {t('drawers.common.attach', { ns: 'missionDashboard' })}
              </ButtonV2>
            </Box>
          ))
        )}
      </Box>
    </DrawerV2>
  );
};

export default ChangeBloomerSocietyDrawer;
